import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import { MdFilterList, MdAddCircle, MdEdit, MdDelete, MdAssignmentReturn, MdCheckCircle } from "react-icons/md";
import loading from "../../assets/images/common/loading.gif";
import { Modal, Row, Col, Form, Button, OverlayTrigger, Badge } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import AppDataGrid from "../../components/common/AppDataGrid";

const InventoryIssueList = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [issues, setIssues] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const [filter, setFilter] = useState({
    item_name: "",
    recipient_name: "",
    status: "",
    issue_date: "",
    action: "FILTER",
  });

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [currentIssue, setCurrentIssue] = useState(null);
  const [returnData, setReturnData] = useState({
    quantity: "",
    remarks: "",
  });

  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(baseUrl + "/inventorytransaction/", { 
        action: "READ",
        type: "ISSUE",
        school_id: userObj.school_id 
      });
      setIssues(response.data || []);
    } catch (error) {
      console.error("Error fetching issue records:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditClick = (issue) => {
    navigate("/issueinventory", {
      state: { issueData: issue },
    });
  };

  const handleReturnClick = (issue) => {
    if (issue.status === "Returned") {
        toast.info("This item has already been returned.");
        return;
    }
    
    const maxReturnable = Number(issue.quantity) - Number(issue.returned_quantity || 0);

    setCurrentIssue(issue);
    setReturnData({
      quantity: maxReturnable > 0 ? maxReturnable : "",
      remarks: "",
    });
    setShowReturnModal(true);
  };

  const handleReturnSubmit = async () => {
    if (!returnData.quantity || returnData.quantity <= 0) {
      toast.warning("Please enter a valid return quantity.");
      return;
    }
    const maxReturnable = Number(currentIssue.quantity) - Number(currentIssue.returned_quantity || 0);
    if (Number(returnData.quantity) > maxReturnable) {
      toast.warning(`Return quantity cannot exceed remaining issued quantity (${maxReturnable}).`);
      return;
    }

    try {
      await axios.post(baseUrl + "/inventorytransaction/", {
        transaction_id: currentIssue.transaction_id,
        item_id: currentIssue.item_id,
        quantity: returnData.quantity,
        remarks: returnData.remarks,
        type: "ISSUE",
        action: "UPDATE",
        status: "Returned",
        actual_return_date: new Date().toISOString().split("T")[0],
        school_id: userObj.school_id,
        lastmodifiedby: userObj.user_id || userObj.userid,
        updated_by: userObj.user_id || userObj.userid
      });
      toast.success("Item marked as returned and stock updated!");
      setShowReturnModal(false);
      fetchData();
    } catch (error) {
      console.error("Error returning item:", error);
      toast.error("Failed to process return");
    }
  };

  const handleCloseReturnModal = () => setShowReturnModal(false);

  const handleDeleteClick = async (transaction_id) => {
    if (!window.confirm("Are you sure you want to delete this issue record?")) return;

    try {
      await axios.post(baseUrl + "/inventorytransaction/", {
        transaction_id,
        action: "DELETE",
        school_id: userObj.school_id,
        lastmodifiedby: userObj.user_id
      });
      toast.success("Record deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record");
    }
  };

  const handleFilterSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(baseUrl + "/inventorytransaction/", {
        ...filter,
        issue_date: filter.issue_date || null,
        type: "ISSUE",
        school_id: userObj.school_id
      });
      setIssues(response.data || []);
      setShowFilterModal(false);
    } catch (error) {
      console.error("Error filtering records:", error);
    }
  };

  const handleFilterClear = () => {
    setFilter({ item_name: "", recipient_name: "", status: "", issue_date: "", action: "FILTER" });
    fetchData();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("T")[0].split("-");
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  const columns = [
    {
      field: "issue_date",
      headerName: "Issue Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={formatDate(params.value)}><span>{formatDate(params.value)}</span></Tooltip>,
      sortable: true,
    },
    {
      field: "item_name",
      headerName: "Item",
      flex: 1.5,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true,
    },
    {
      field: "recipient_name",
      headerName: "Recipient",
      flex: 1.5,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true,
    },
    {
        field: "status",
        headerName: "Status",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Badge bg={params.value === "Returned" ? "success" : "warning"} className="text-capitalize">
            {params.value || "Issued"}
          </Badge>
        ),
        sortable: true,
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 0.6,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true,
    },
    {
      field: "expected_return_date",
      headerName: "Expected Return",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={formatDate(params.value)}><span>{formatDate(params.value) || "-"}</span></Tooltip>,
      sortable: true,
    },
    {
      field: "returned_quantity",
      headerName: "Returned Quantity",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value || 0}><span>{params.value || 0}</span></Tooltip>,
      sortable: true,
    },
    {
      field: "return_remarks",
      headerName: "Return Remarks",
      flex: 1.2,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value || ""}><span>{params.value || "-"}</span></Tooltip>,
      sortable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.2,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => (
        <div className="tableActions">
          <Tooltip title="Mark as Returned" arrow>
            <span 
                className={`commonActionIcons ${params.row.status === "Returned" ? "text-success" : ""}`} 
                onClick={() => handleReturnClick(params.row)}
            >
              {params.row.status === "Returned" ? <MdCheckCircle /> : <MdAssignmentReturn />}
            </span>
          </Tooltip>

        </div>
      ),
    },
  ];

  const filteredRecords = issues.filter((iss) => {
    const query = searchQuery.toLowerCase();
    return (
      (iss.item_name || "").toLowerCase().includes(query) ||
      (iss.recipient_name || "").toLowerCase().includes(query) ||
      formatDate(iss.issue_date).toLowerCase().includes(query) ||
      formatDate(iss.expected_return_date).toLowerCase().includes(query) ||
      String(iss.quantity ?? "").toLowerCase().includes(query) ||
      String(iss.returned_quantity ?? "").toLowerCase().includes(query)
    );
  });

  return (
    <div className="pageBody">
      <ToastContainer />
      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Inventory Issue List</h6>
          </div>
          <div className="">
            <input
              type="text"
              placeholder="Search..."
              className="searchInput"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger placement="top" overlay={<Tooltip>Filter</Tooltip>}>
              <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                <MdFilterList />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Add</Tooltip>}>
              <Button className="primaryBtn" variant="primary" onClick={() => navigate("/issueinventory")}>
                <MdAddCircle />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      </div>

      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords}
              columns={columns}
              getRowId={(row) => row.transaction_id}
              pageSizeOptions={[10, 20, 50]}
              showToolbar={false}
              pagination
              density="compact"
              maxHeight={550}
            />
          )}
        </div>
      </div>

      <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Form onSubmit={handleFilterSubmit}>
            <Row>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group className="mb-3">
                    <Form.Label>Item Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={filter.item_name}
                      onChange={(e) => setFilter({ ...filter, item_name: e.target.value })}
                      placeholder="Enter Item Name"
                    />
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group className="mb-3">
                    <Form.Label>Recipient Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={filter.recipient_name}
                      onChange={(e) => setFilter({ ...filter, recipient_name: e.target.value })}
                      placeholder="Enter Recipient Name"
                    />
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group className="mb-3">
                    <Form.Label>Issue Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={filter.issue_date}
                      onChange={(e) => setFilter({ ...filter, issue_date: e.target.value })}
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleFilterClear}>
            Reset
          </Button>
          <div>
            <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
              Cancel
            </Button>
            <Button variant="primary" className="btn-success primaryBtn" onClick={handleFilterSubmit}>
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Return Item Modal */}
      <Modal show={showReturnModal} onHide={handleCloseReturnModal} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Return Item</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Form>
            <Row>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group className="mb-3">
                    <Form.Label>Quantity to Return</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max={currentIssue ? Number(currentIssue.quantity) - Number(currentIssue.returned_quantity || 0) : ""}
                      value={returnData.quantity}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (val !== "") {
                          val = Math.max(1, Number(val));
                          const maxReturnable = currentIssue ? Number(currentIssue.quantity) - Number(currentIssue.returned_quantity || 0) : 0;
                          if (val > maxReturnable) val = maxReturnable;
                        }
                        setReturnData({ ...returnData, quantity: val });
                      }}
                      placeholder="Enter Quantity to Return"
                    />
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group className="mb-3">
                    <Form.Label>Remarks</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={returnData.remarks}
                      onChange={(e) => setReturnData({ ...returnData, remarks: e.target.value })}
                      placeholder="Enter Remarks (optional)"
                    />
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <div>
            <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseReturnModal}>
              Cancel
            </Button>
            <Button variant="primary" className="btn-success primaryBtn" onClick={handleReturnSubmit}>
              Submit Return
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InventoryIssueList;
